import React from 'react';
import { GetStaticPaths, GetStaticProps } from 'next';
import Head from 'next/head';

import {
  Teacher,
  TeacherDocument,
  TeachersDocument,
  TeacherEndorsement,
  LatestVideosDocument,
  Video,
} from '@edx/graphql/generated';
import { useAppConfigStateContext } from '@edx/contexts/app-config';
import { getStaticApolloClient } from '@edx/helpers/apollo';
import { useAuth } from '@edx/hooks/use-auth';
import { Hero } from '@edx/components/hero';
import { Link } from '@edx/components/link';
import { TeacherProfile } from '@edx/components/teacher-profile';
import { Avatar } from '@edx/components/avatar';
import { EnrollBanner } from '@edx/components/banners';
import { IconAngleLeft } from '@edx/components/icons';
import { TeacherGallery } from '@edx/components/teacher-gallery';
import { Intro } from '@edx/components/typography';
import { VideoSlider } from '@edx/components/video-slider';

type Props = {
  teacher: Teacher;
  latestVideos: Video[];
};

export const getStaticPaths: GetStaticPaths = async () => {
  const { apolloClient } = await getStaticApolloClient();
  const { data } = await apolloClient.query({ query: TeachersDocument });

  const paths = data.teachers.map(({ slug }: Teacher) => ({ params: { slug } }));

  return {
    paths,
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps = async (context) => {
  const { apolloClient } = await getStaticApolloClient();

  const slug = context.params?.slug;
  const teacherQuery = await apolloClient.query({ query: TeacherDocument, variables: { slug } });
  const teacher = teacherQuery.data.teacher;

  const latestVideosQuery = await apolloClient.query({
    query: LatestVideosDocument,
    variables: { filter: { teacherId: teacher.id } },
  });

  return {
    props: {
      initialApolloState: apolloClient.cache.extract(),
      teacher,
      latestVideos: latestVideosQuery.data.latestVideos,
    },
  };
};

const TeacherPage: React.FC<Props> = ({ teacher, latestVideos }) => {
  const { projectShortName } = useAppConfigStateContext();
  const { isLoggedOut } = useAuth();

  return (
    <>
      <Head>
        {teacher.metaTitle && <title key="title">{teacher.metaTitle}</title>}

        {teacher.metaDescription && <meta key="description" name="description" content={teacher.metaDescription} />}

        {/* Open Graph */}
        {teacher.metaTitle && <meta key="og:title" property="og:title" content={teacher.metaTitle} />}
        {teacher.metaDescription && (
          <meta key="og:description" property="og:description" content={teacher.metaDescription} />
        )}
        {teacher.openGraphImageUrl && <meta key="og:image" property="og:image" content={teacher.openGraphImageUrl} />}

        {!teacher.shouldIndex && <meta name="robots" content="noindex" />}
      </Head>

      <Hero heading={teacher.name} preheading="· Profesores ·" bgImage={teacher.images.header} />

      <div className="-mt-20 max-w-6xl mx-auto px-4 md:px-6 lg:px-10 py-1 md:py-4">
        <TeacherGallery images={teacher.images.gallery} />

        <div className="py-12 lg:py-20 px-4 lg:px-24 font-body">
          <section className="grid grid-cols-max md:grid-cols-12 gap-10">
            <div className="col-span-full md:col-span-7 space-y-4">
              <div className="flex items-center text-gray-300 group transform transition-space duration-100 ease space-x-1 hover:space-x-2 hover:ml-2">
                <IconAngleLeft className="w-5 h-5 transition duration-100 group-hover:text-primary-500" />
                <Link className="uppercase tracking-widest" href="/profesores">
                  Volver a profesores
                </Link>
              </div>

              <div
                className="prose prose-dark text-body-large text-gray-500 leading-relaxed space-y-4"
                dangerouslySetInnerHTML={{ __html: teacher.bodyHTML }}
              />
            </div>

            <div className="col-span-full md:col-span-5 pl-0 lg:pl-10">
              <TeacherProfile
                name={teacher.name}
                tagline={teacher.tagline}
                avatarUrl={teacher.avatarUrl}
                location={teacher.location}
                yearBorn={teacher.yearBorn}
                facebookUrl={teacher.facebookUrl}
                instagramUrl={teacher.instagramUrl}
                twitterUrl={teacher.twitterUrl}
                youtubeUrl={teacher.youtubeUrl}
                endorsements={teacher.endorsements as TeacherEndorsement[]}
              />
            </div>
          </section>
        </div>
      </div>

      {latestVideos.length > 0 && (
        <section className="bg-gray-800">
          <div className="max-w-6xl mx-auto px-4 md:px-10 pt-16 pb-10">
            <div className="flex space-x-4 items-center mb-10">
              <Avatar name={teacher.name} src={teacher.avatarUrl} size={64} />{' '}
              <Intro className="text-gray-400">Últimos videos en {projectShortName}</Intro>
            </div>

            <VideoSlider items={latestVideos} />
          </div>
        </section>
      )}

      {isLoggedOut && <EnrollBanner />}
    </>
  );
};

export default TeacherPage;
